import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import "./Commission.css";
import { Button } from "@mui/material";
import Commissiontable from "./CommissionTable";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ApiConfig from "../../common/config/ApiConfig";

// /GetDownlinepayrate paginates `rows` (default 20, max 100) — `stakes`
// is never paginated. Same shape/behavior as /getdownlinecommission, just
// keyed by `payrates`/`payrate` instead of `commissions`/`commission`.
const PAGE_LIMIT = 10;

// Which downline tiers (`position` in the request body) the current user's
// own AdminLogin.position is allowed to query — a mismatch 403s server-side,
// so this filters the tier selector down to only the valid options.
const POSITION_TIER_OPTIONS = {
  owner: [
    { value: "senior", label: "Senior" },
    { value: "master", label: "Master" },
    { value: "agent", label: "Agent" },
  ],
  senior: [
    { value: "master", label: "Master" },
    { value: "agent", label: "Agent" },
  ],
  master: [{ value: "agent", label: "Agent" }],
};

function Reward() {
  const pos = localStorage.getItem("position");
  const username = localStorage.getItem("user");
  const location = useLocation();

  const positionOptions = POSITION_TIER_OPTIONS[pos] || [];

  const [lottoTypes, setLottoTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(
    positionOptions[0]?.value || "",
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState({
    stakes: [],
    rows: [],
    upline: null,
  });

  useEffect(() => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  }, []);

  // Default the filter to the first lotto type once the list loads.
  useEffect(() => {
    if (lottoTypes.length > 0 && !selectedType) {
      setSelectedType(lottoTypes[0].type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottoTypes]);

  const fetchPayrateForType = async (type, pageNum, positionTier) => {
    const params = new URLSearchParams({
      agent: username,
      type,
      position: positionTier,
      page: pageNum,
      limit: PAGE_LIMIT,
    });
    const result = await ApiConfig.get(
      `/GetDownlinepayrate?${params.toString()}`,
    );
    if (result.resCode !== 200) {
      toast.error(result.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setTableData({
      stakes: result.data?.stakes || [],
      rows: result.data?.rows || [],
      upline: result.data?.upline || null,
    });
    setTotal(result.data?.total ?? 0);
  };

  useEffect(() => {
    if (!selectedType || !selectedPosition) return;
    fetchPayrateForType(selectedType, page, selectedPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, page, selectedPosition]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  // state for the edit dialog

  const [editvisible, setEditvisible] = useState(false);
  const [agent, setAgent] = useState("");
  const [edittype, setEdittype] = useState("");
  const [editStakes, setEditStakes] = useState([]);
  // [{stake, id, payrate}, ...] built directly from the clicked row's own
  // `payrates` object (each cell already carries its Payrate `id` — no extra
  // lookup round-trip needed). `id` is null when no row exists yet for that
  // agent/stake; those stakes can't be saved via /SetIDpayrate (it only
  // updates existing rows), so their input stays disabled.
  const [editRows, setEditRows] = useState([]);
  const [editValues, setEditValues] = useState({});
  // The requesting agent's own payrate per stake — `upline.payrates` from
  // /GetDownlinepayrate (already fetched for the current table, no extra
  // call needed). Shown as a cap hint and enforced client-side; the same
  // cap is also enforced server-side on /SetIDpayrate per the API docs, so
  // this is a UX nicety, not the only guard.
  const editUplinePayrates = tableData.upline?.payrates || {};

  const handleEdit = (row, type) => {
    setEdittype(type);
    setAgent(row.username);
    setEditStakes(tableData.stakes || []);

    const stakeRows = Object.entries(row.payrates || {}).map(
      ([stake, cell]) => ({
        stake,
        id: cell?.id ?? null,
        payrate: Number(cell?.payrate ?? 0),
      }),
    );
    setEditRows(stakeRows);
    setEditValues(
      stakeRows.reduce((acc, r) => ({ ...acc, [r.stake]: r.payrate }), {}),
    );
    setEditvisible(true);
  };

  const handleClose = () => {
    setEditvisible(false);
    setEdittype("");
    setAgent("");
    setEditStakes([]);
    setEditRows([]);
    setEditValues({});
  };

  // Reward entered for a stake can never exceed the requesting agent's own
  // payrate for that stake.
  const handleChangePayrate = (stake, rawValue) => {
    const uplineCell = editUplinePayrates[stake];
    const uplineValue = uplineCell?.payrate;
    let value = Number(rawValue);
    if (isNaN(value) || value < 0) value = 0;
    if (uplineValue !== undefined && value > Number(uplineValue)) {
      value = Number(uplineValue);
      Swal.fire({
        title: "ตรวจสอบข้อมูลให้ถูกต้อง",
        text: `รางวัลไม่ควรเกิน ${Number(uplineValue)}`,
        icon: "error",
      });
    }
    setEditValues((prev) => ({ ...prev, [stake]: value }));
  };

  // /SetIDpayrate updates one Payrate row (by id) at a time. Stakes with no
  // existing row (id === null) are skipped — the endpoint can only update a
  // row that already exists.
  const handleSave = async () => {
    const savableRows = editRows.filter((row) => row.id !== null);
    const results = await Promise.all(
      savableRows.map((row) =>
        ApiConfig.post("/SetIDpayrate", {
          id: row.id,
          value: { payrate: editValues[row.stake] ?? 0 },
        }),
      ),
    );
    if (results.some((r) => r.resCode !== 200)) {
      toast.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล", { autoClose: 2000 });
      return;
    }
    toast.success("บันทึกข้อมูลสำเร็จ", { autoClose: 2000 });
    fetchPayrateForType(edittype, page, selectedPosition);
    handleClose();
  };

  // Columns are built from `stakes` (per lotto type), not hardcoded — a
  // stake added/removed from the reward-type catalog is picked up on the
  // next fetch without a frontend deploy.
  const buildColumns = (stakes, type) => [
    {
      Header: "ลำดับ",
      id: "index",
      align: "center",
      widthClass: "w-[50px]",
      width: 50,
      sticky: true,
      stickyLeft: 0,
      accessor: () => null,
      Cell: ({ row }) => (
        <span className="text-xs font-bold">{row.index + 1}</span>
      ),
    },
    {
      Header: "Username",
      accessor: "username",
      align: "center",
      widthClass: "w-1/12",
      width: 130,
      sticky: true,
      stickyLeft: 50,
      stickyShadow: true,
      Cell: ({ value, row }) => (
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs font-bold">{value}</span>
          <Button
            onClick={() => handleEdit(row.original, type)}
            className="font-bold"
            size="small"
            sx={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}
          >
            Edit
          </Button>
        </div>
      ),
    },
    ...stakes.map((s) => ({
      Header: s.name,
      id: s.code,
      accessor: (row) => row.payrates?.[s.code]?.payrate ?? 0,
      align: "center",
      Cell: ({ value }) => <span className="text-xs font-bold">{value}</span>,
      widthClass: "w-1/12",
    })),
  ];

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title="Reward" />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-3">
            <div className="flex flex-wrap gap-2 p-2 bg-white rounded-lg shadow-sm">
              <Link to="/commission">
                <Button
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant={
                    location.pathname === "/commission" ? "contained" : "text"
                  }
                >
                  ค่าคอมมิชชั่น
                </Button>
              </Link>
              <Link to="/reward">
                <Button
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant={
                    location.pathname === "/reward" ? "contained" : "text"
                  }
                >
                  รางวัล
                </Button>
              </Link>
              <Link to="/maxbet">
                <Button
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant={
                    location.pathname === "/maxbet" ? "contained" : "text"
                  }
                >
                  แทงสูงสุด
                </Button>
              </Link>
              <Link to="/minbet">
                <Button
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant={
                    location.pathname === "/minbet" ? "contained" : "text"
                  }
                >
                  แทงต่ำสุด
                </Button>
              </Link>
              <Link to="/maxbetpermatch">
                <Button
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant={
                    location.pathname === "/maxbetpermatch"
                      ? "contained"
                      : "text"
                  }
                >
                  แทงสูงสุดต่อเลข
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-4 p-2 bg-sky-50 border border-sky-200 rounded-lg">
              <div className="flex items-center gap-2">
                <label className="font-bold text-xs text-sky-800">
                  กรองตามประเภทหวย:
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    setPage(1);
                  }}
                  className="border border-sky-300 bg-white rounded px-2 py-1 text-xs font-bold text-sky-800"
                >
                  {lottoTypes.map((lt) => (
                    <option key={lt.id} value={lt.type}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-bold text-xs text-sky-800">
                  ระดับดาวน์ไลน์:
                </label>
                <select
                  value={selectedPosition}
                  onChange={(e) => {
                    setSelectedPosition(e.target.value);
                    setPage(1);
                  }}
                  className="border border-sky-300 bg-white rounded px-2 py-1 text-xs font-bold text-sky-800"
                >
                  {positionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-2">
              <Commissiontable
                columns={buildColumns(tableData.stakes, selectedType)}
                data={tableData.rows}
              />
            </div>

            <div className="flex items-center justify-center gap-3 p-2 bg-white rounded-lg shadow-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 text-xs font-bold text-white bg-sky-600 hover:bg-sky-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                ก่อนหน้า
              </button>
              <span className="text-xs font-bold text-slate-700">
                หน้า {page} / {totalPages}{" "}
                <span className="text-slate-400 font-normal">
                  (ทั้งหมด {total} รายการ)
                </span>
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 text-xs font-bold text-white bg-sky-600 hover:bg-sky-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      </div>

      {editvisible && (
        <div>
          <Dialog
            open={editvisible}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: { width: "90%", maxWidth: "95%", margin: 0 },
            }}
          >
            <DialogTitle
              className="text-white bg-brand-blue-800"
              id="alert-dialog-title"
              sx={{ fontSize: "1rem", py: 1 }}
            >
              <div className="p-1 flex justify-items-center items-center flex-row space-x-2">
                <span className="font-bold text-sm">ปรับรางวัล {agent}</span>
                {tableData.upline?.username && (
                  <span className="text-xs font-normal text-brand-blue-200">
                    (อัพไลน์: {tableData.upline.username})
                  </span>
                )}
              </div>
            </DialogTitle>
            <DialogContent>
              <div className="flex w-full flex-col space-y-2 overflow-x-auto">
                <table className="w-[100%] border-collapse">
                  <thead>
                    <tr className="transition-colors">
                      {editStakes.map((s) => (
                        <th
                          key={s.code}
                          className="p-2 min-w-[120px] text-xs bg-brand-blue-100 text-brand-blue-900 font-bold rounded"
                        >
                          {s.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="transition-colors hover:bg-slate-100">
                      {editStakes.map((s) => {
                        const uplineValue = editUplinePayrates[s.code]?.payrate;
                        const editRow = editRows.find(
                          (r) => r.stake === s.code,
                        );
                        const noRowYet = !editRow || editRow.id === null;
                        return (
                          <td key={s.code} className="p-2 min-w-[120px]">
                            <span className="font-bold text-amber-600 justify-items-center items-center text-[10px]">
                              สูงสุด{" "}
                              {uplineValue !== undefined
                                ? Number(uplineValue)
                                : 0}
                            </span>
                            <div className="flex items-center">
                              <input
                                className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-bold text-slate-700 focus:outline-none focus:border-sky-500 disabled:bg-slate-100 disabled:text-slate-400"
                                disabled={noRowYet}
                                onChange={(e) =>
                                  handleChangePayrate(s.code, e.target.value)
                                }
                                onClick={(e) => e.target.select()}
                                value={editValues[s.code] ?? 0}
                                type="number"
                              />
                            </div>
                            {noRowYet && (
                              <span className="block text-[10px] text-red-500 font-bold">
                                ยังไม่มีข้อมูล
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </DialogContent>
            <DialogActions>
              <button
                onClick={() => handleSave()}
                className="bg-green-500 hover:bg-green-700 text-white text-xs font-bold py-1 px-3 rounded"
              >
                บันทึก
              </button>

              <button
                onClick={handleClose}
                className="bg-red-500 hover:bg-red-700 text-white text-xs font-bold py-1 px-3 rounded"
              >
                ยกเลิก
              </button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
}

export default Reward;
